import { useState } from 'react';
import useUploadService, { UploadMediaType } from '../api/edits/upload.service';

export function useUploadFile() {
  const { initiateUpload, completeUpload } = useUploadService();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const uploadFile = async (
    routeId: string,
    file: File,
    mediaType: UploadMediaType,
    filename: string,
    languageCode?: string
  ): Promise<{ uploadSessionId: string; filename: string }> => {
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      const initResponse = await initiateUpload(routeId, {
        mediaType,
        filename,
        languageCode,
        contentType: file.type,
      });
      const { uploadSessionId, uploadUrl } = initResponse.data;

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!putResponse.ok) {
        throw new Error(`Storage upload failed (${putResponse.status})`);
      }

      const etag = putResponse.headers.get('ETag') ?? undefined;

      await completeUpload(routeId, {
        uploadSessionId,
        etag,
        bytes: file.size,
      });

      setUploadSuccess(true);
      return { uploadSessionId, filename };
    } catch (err: any) {
      const msg = err?.message ?? 'Upload failed';
      setUploadError(msg);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return { uploadFile, uploading, uploadError, uploadSuccess };
}
